import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const TravelInfoTab = ({ travelInfo }) => {
  const { t, currentLanguage } = useLanguage();

  const InfoCard = ({ icon, title, children }) => (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6">
      <h4 className="text-base md:text-lg font-medium text-foreground mb-4 md:mb-6 flex items-center gap-2">
        <Icon name={icon} size={20} className="text-primary" />
        {title}
      </h4>
      {children}
    </div>
  );

  const InfoRow = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-border last:border-0 gap-1 sm:gap-4">
      <span className="caption text-muted-foreground">{label}</span>
      <span className="text-sm md:text-base font-medium text-foreground">{value}</span>
    </div>
  );

  return (
    <div className="space-y-6 md:space-y-8">
      <h3 className="text-lg md:text-xl font-semibold text-foreground">
        {t('pilgrimProfile.travelInfo')}
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <InfoCard icon="Users" title={t('pilgrimProfile.groupAssignment')}>
          <div className="space-y-3">
            <InfoRow 
              label={t('pilgrimProfile.groupName')} 
              value={travelInfo?.groupName} 
            />
            <InfoRow 
              label={t('pilgrimProfile.groupType')} 
              value={travelInfo?.groupType} 
            />
            <InfoRow 
              label={t('pilgrimProfile.departureDate')} 
              value={new Date(travelInfo.departureDate)?.toLocaleDateString('en-US')} 
            />
            <InfoRow 
              label={t('pilgrimProfile.returnDate')} 
              value={new Date(travelInfo.returnDate)?.toLocaleDateString('en-US')} 
            />
          </div>
        </InfoCard>

        <InfoCard icon="Building" title={t('pilgrimProfile.accommodation')}>
          <div className="space-y-3">
            <InfoRow 
              label={t('pilgrimProfile.makkahHotel')} 
              value={travelInfo?.makkahHotel} 
            />
            <InfoRow 
              label={t('pilgrimProfile.madinahHotel')} 
              value={travelInfo?.madinahHotel} 
            />
            <InfoRow 
              label={t('pilgrimProfile.roomType')} 
              value={travelInfo?.roomType} 
            />
          </div>
        </InfoCard>

        <InfoCard icon="Plane" title={t('pilgrimProfile.flightInfo')}>
          <div className="space-y-4 md:space-y-6">
            <div>
              <p className="text-sm font-medium text-foreground mb-3">
                {t('pilgrimProfile.outboundFlight')}
              </p>
              <div className="space-y-2">
                <InfoRow 
                  label={t('pilgrimProfile.flightNumber')} 
                  value={travelInfo?.outboundFlight?.flightNumber} 
                />
                <InfoRow 
                  label={t('pilgrimProfile.airline')} 
                  value={travelInfo?.outboundFlight?.airline} 
                />
                <InfoRow 
                  label={t('pilgrimProfile.departure')} 
                  value={`${travelInfo?.outboundFlight?.departure} - ${new Date(travelInfo.outboundFlight.departureTime)?.toLocaleString('en-US')}`} 
                />
                <InfoRow 
                  label={t('pilgrimProfile.arrival')} 
                  value={`${travelInfo?.outboundFlight?.arrival} - ${new Date(travelInfo.outboundFlight.arrivalTime)?.toLocaleString('en-US')}`} 
                />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground mb-3">
                {t('pilgrimProfile.returnFlight')}
              </p>
              <div className="space-y-2">
                <InfoRow 
                  label={t('pilgrimProfile.flightNumber')} 
                  value={travelInfo?.returnFlight?.flightNumber} 
                />
                <InfoRow 
                  label={t('pilgrimProfile.airline')} 
                  value={travelInfo?.returnFlight?.airline} 
                />
                <InfoRow 
                  label={t('pilgrimProfile.departure')} 
                  value={`${travelInfo?.returnFlight?.departure} - ${new Date(travelInfo.returnFlight.departureTime)?.toLocaleString('en-US')}`} 
                />
                <InfoRow 
                  label={t('pilgrimProfile.arrival')} 
                  value={`${travelInfo?.returnFlight?.arrival} - ${new Date(travelInfo.returnFlight.arrivalTime)?.toLocaleString('en-US')}`} 
                />
              </div>
            </div>
          </div>
        </InfoCard>

        <InfoCard icon="RefreshCw" title={t('pilgrimProfile.nusukSync')}>
          <div className="space-y-3">
            <InfoRow 
              label={t('pilgrimProfile.status')} 
              value={
                <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                  travelInfo?.nusukSync?.status === 'synced' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                }`}>
                  {travelInfo?.nusukSync?.status === 'synced' ? t('pilgrimProfile.synced') 
                    : t('pilgrimProfile.pending')}
                </span>
              } 
            />
            <InfoRow 
              label={t('pilgrimProfile.lastSync')} 
              value={new Date(travelInfo.nusukSync.lastSync)?.toLocaleString('en-US')} 
            />
          </div>
        </InfoCard>
      </div>
      <div className="bg-primary/5 rounded-lg border border-primary/20 p-4 md:p-6">
        <div className="flex items-start gap-3">
          <Icon name="MapPin" size={24} className="text-primary flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h4 className="text-base md:text-lg font-medium text-foreground mb-2">
              {t('pilgrimProfile.itinerary')}
            </h4>
            <p className="caption text-muted-foreground mb-4">
              {travelInfo?.itinerary}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelInfoTab;